import { Component, OnInit } from '@angular/core';
import { SurveyService } from 'src/app/services/survey/survey.service';
import { TrackingService } from 'src/app/services/survey/tracking.service';
import { AnswerService } from 'src/app/services/survey/answer.service';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-monitoring',
  templateUrl: './monitoring.component.html',
  styleUrls: ['./monitoring.component.scss']
})
export class MonitoringComponent implements OnInit {

  // Encuestas y personas
  surveys: any[] = [];
  filteredSurveys: any[] = [];
  persons: any[] = [];
  filteredPersons: any[] = [];
  selectedSurvey: any = null;
  selectedPerson: any = null;

  // Respuestas
  answers: any[] = [];
  paginatedAnswers: any[] = [];
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number[] = [];
  isLoadingSurveys = true;
  isLoadingPersons = false;
  isLoadingAnswers = false;

  // Filtros
  searchSurvey = '';
  searchPerson = '';
  personStatusFilter = 'all';
  filteredAnswers: any[] = []; // Lista filtrada
  uniqueSections: string[] = []; // Lista de secciones únicas
  selectedSection: string = 'all'; // Sección seleccionada para el filtro
  selectedPersons: any[] = [];  // Para almacenar las personas seleccionadas

  // Variables para contar personas
  finishedCount: number = 0;
  startedCount: number = 0;
  totalCount: number = 0;

  // Mensajes de error
  errorMessage: string | null = null;

  constructor(
    private surveyService: SurveyService,
    private trackingService: TrackingService,
    private answerService: AnswerService
  ) {}

  ngOnInit(): void {
    this.loadSurveys();
  }

  // Cargar todas las encuestas
  loadSurveys(): void {
    this.isLoadingSurveys = true;
    this.surveyService.getAll().subscribe(
      (data: any) => {
        this.surveys = data.data;
        this.filteredSurveys = [...this.surveys];
        this.isLoadingSurveys = false;
      },
      (error) => {
        this.isLoadingSurveys = false;
        this.errorMessage = 'Error al cargar las encuestas.';
      }
    );
  }

  // Filtrar encuestas por texto
  filterSurveys(): void {
    const searchTerm = this.searchSurvey.toLowerCase();
    this.filteredSurveys = this.surveys.filter(survey =>
      survey.title.toLowerCase().includes(searchTerm)
    );
  }

  // Seleccionar una encuesta y cargar personas
  selectSurvey(survey: any): void {
    this.selectedSurvey = survey;

    // Reiniciar las personas y las respuestas
    this.persons = [];
    this.filteredPersons = [];
    this.selectedPersons = [];
    this.answers = [];
    this.paginatedAnswers = [];
    this.filteredAnswers = [];
    this.uniqueSections = [];
    this.selectedSection = 'all';
    this.currentPage = 1;

    // Cargar personas para la nueva encuesta seleccionada
    this.loadPersons();
  }

  // Cargar personas que han respondido a la encuesta seleccionada
  loadPersons(): void {
    this.isLoadingPersons = true;
    this.errorMessage = null; // Reseteamos el mensaje de error antes de intentar cargar
    this.trackingService.getRespondentsBySurvey(this.selectedSurvey.id).subscribe(
      (data: any) => {
        this.persons = data.data;
        this.filterPersons();
        this.isLoadingPersons = false;
      },
      (error) => {
        this.isLoadingPersons = false;
        this.errorMessage = error; // Asignamos el mensaje de error recibido
      }
    );
  }

  // Filtrar personas según el estado seleccionado
  filterPersons(): void {

    // Filtrar por estado
    let filteredByStatus = this.persons;
    if (this.personStatusFilter !== 'all') {
      filteredByStatus = this.persons.filter(person => person.status === this.personStatusFilter);
    }

    // Filtrar por texto
    if (this.searchPerson) {
      this.filteredPersons = filteredByStatus.filter(person =>
        person.email.toLowerCase().includes(this.searchPerson.toLowerCase())
      );
    } else {
      this.filteredPersons = filteredByStatus;
    }

    this.finishedCount = this.persons.filter(person => person.status === 'finished').length;
    this.startedCount = this.persons.filter(person => person.status === 'started').length;
    this.totalCount = this.persons.length;

  }

  // Cambiar el filtro de personas (todas, iniciadas, completadas)
  setPersonFilter(status: string): void {
    this.personStatusFilter = status;
    this.filterPersons();
  }

  // Seleccionar una persona y cargar sus respuestas
  selectPerson(person: any): void {
    this.selectedPersons = [person];  // Aseguramos que solo la persona seleccionada esté en la lista
    this.loadSelectedPersonsAnswers();  // Llamamos al método que carga las respuestas
}

  // Cargar respuestas de la persona seleccionada
  loadAnswers(): void {
    this.isLoadingAnswers = true;
    this.errorMessage = null; // Reseteamos el mensaje de error antes de intentar cargar
    this.answerService.getAllUuid(this.selectedPerson.uuid).subscribe(
      (data: any) => {
        this.answers = data.data;
        this.filteredAnswers = [...this.answers];
        this.extractUniqueSections();
        this.selectedSection = 'all'; // Reiniciar el filtro de sección
        this.currentPage = 1; // Reiniciar la paginación
        this.paginateAnswers();
        this.isLoadingAnswers = false;
      },
      (error) => {
        this.isLoadingAnswers = false;
        this.errorMessage = error; // Asignamos el mensaje de error recibido
      }
    );
  }

  // Extraer secciones únicas de las respuestas
  extractUniqueSections(): void {
    const sections = this.answers.map(answer => answer.section).filter(Boolean);
    this.uniqueSections = Array.from(new Set(sections));
  }

  // Filtrar respuestas por sección
  filterAnswersBySection(): void {
    this.filteredAnswers = this.selectedSection === 'all'
      ? [...this.answers]
      : this.answers.filter(answer => answer.section === this.selectedSection);

    this.currentPage = 1;
    this.paginateAnswers();
  }

  // Paginación de respuestas
  paginateAnswers(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    this.paginatedAnswers = this.filteredAnswers.slice(start, start + this.pageSize);
    this.totalPages = Array.from({ length: Math.ceil(this.filteredAnswers.length / this.pageSize) }, (_, i) => i + 1);
  }

  // Cambiar página en la paginación
  changePage(page: number): void {
    this.currentPage = page;
    this.paginateAnswers();
  }

  // Obtener el estado de la encuesta en texto
  getSurveyStatusText(status: number): string {
    const statusMap: { [key: number]: string } = {
      1: 'Abierta',
      2: 'Cerrada',
      3: 'Borrador',
      4: 'Test'
    };
    return statusMap[status] || 'Desconocido';
  }

  // Obtener el estado de la persona en texto
  getPersonStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      finished: 'Completada',
      started: 'Iniciada'
    };
    return statusMap[status] || 'Desconocido';
  }

  // Obtener detalles de la pregunta (sección, pregunta y axis)
  getQuestionDetails(answer: any): string {
    const { section, question, axis } = answer;
    return `${section || 'Sección desconocida'} - ${question || 'Pregunta desconocida'}${axis ? ' - ' + axis : ''}`;
  }

  convertAnswersToCSV(answers: any[]): string {
    if (!answers || answers.length === 0) {
      return '';
    }

    const headers = ['ID', 'ID_Usuario', 'Pregunta', 'Respuesta', 'Axis', 'Sección', 'OS', 'Navegador'];
    const rows = answers.map(answer => [
      answer.id,
      answer.uuid,
      answer.question || 'Pregunta desconocida',
      answer.answer || 'Sin respuesta',
      answer.axis || '',
      answer.section || 'Sección desconocida',
      answer.os || '',
      answer.browser || ''
    ]);

    const csvContent = [
      headers.join(','), // Encabezados
      ...rows.map(row => row.join(',')) // Filas
    ].join('\n');

    return csvContent;
  }

  downloadCSV(): void {
    if (!this.filteredAnswers || this.filteredAnswers.length === 0) {
      alert('No hay respuestas para descargar.');
      return;
    }

    const csvContent = this.convertAnswersToCSV(this.filteredAnswers);

    if (!csvContent) {
      alert('No hay respuestas para descargar.');
      return;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Obtener la fecha y hora actual
    const now = new Date();
    const date = now.toISOString().slice(0, 10); // yyyy-mm-dd
    const time = now.toTimeString().slice(0, 8).replace(/:/g, ''); // hhmmss

    // Obtener el nombre de la encuesta
    const surveyTitle = this.selectedSurvey ? this.selectedSurvey.title : 'encuesta';

    // Generar el nombre del archivo
    const fileName = `${date}_${time}_${surveyTitle}.csv`;

    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }



  togglePersonSelection(person: any): void {
    const index = this.selectedPersons.findIndex(p => p.uuid === person.uuid);
    if (index === -1) {
      this.selectedPersons.push(person);
    } else {
      this.selectedPersons.splice(index, 1);
    }
    this.loadSelectedPersonsAnswers();
  }

  isPersonSelected(person: any): boolean {
    return this.selectedPersons.some(p => p.uuid === person.uuid);
  }

  loadSelectedPersonsAnswers(): void {
    this.isLoadingAnswers = true;
    this.answers = [];
    this.filteredAnswers = [];
    this.paginatedAnswers = [];
    this.uniqueSections = [];
    this.selectedSection = 'all';
    this.currentPage = 1;

    if (this.selectedPersons.length === 0) {
      this.isLoadingAnswers = false;
      return;
    }

    const answerObservables = this.selectedPersons.map(person =>
      this.answerService.getAllUuid(person.uuid).pipe(
        map((response: any) => response.data.map((answer: any) => ({ ...answer, personEmail: person.email })))
      )
    );

    forkJoin(answerObservables).subscribe((results: any[][]) => {
      this.answers = results.reduce((acc, curr) => acc.concat(curr), []);
      this.filteredAnswers = [...this.answers];
      this.extractUniqueSections();
      this.paginateAnswers();
      this.isLoadingAnswers = false;
    }, (error) => {
      console.error('Error al cargar las respuestas:', error);
      this.isLoadingAnswers = false;
      this.errorMessage = 'Error al cargar las respuestas.';
    });
  }







}
